import { useEffect } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { Shield, Lock, ArrowLeft } from "lucide-react";
import { SiteFooter, INFO_LINKS } from "@/components/SiteFooter";

const H2 = ({ children }) => <h2 className="font-display font-bold text-2xl tracking-tight mt-10 mb-3 text-white">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-display font-bold text-lg mt-6 mb-2 text-[#D6D9DD]">{children}</h3>;
const P = ({ children }) => <p className="text-[var(--txt-secondary)] leading-relaxed mb-4" style={{ fontSize: "17px" }}>{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 text-[var(--txt-secondary)] mb-4" style={{ fontSize: "17px" }}>{children}</ul>;
const Mail = ({ addr }) => <a href={`mailto:${addr}`} className="text-white underline underline-offset-2 hover:text-[var(--urgent)] transition-colors">{addr}</a>;

const ComingSoon = ({ what }) => (
  <>
    <P>{what} will be published here shortly.</P>
    <P>Coming soon . . .</P>
    <P>If you would like to be notified when this page is available, please contact us at <Mail addr="support@tardigradeteam.com" />.</P>
  </>
);

const CONTENT = {
  "about-us": {
    title: "About us",
    body: (
      <>
        <P>We are an independent multi-disciplinary team dedicated to one idea: helping your organisation resist disruptive events by building resilience at each layer.</P>
        <P>Inspired by the tardigrade, the tiny creature known for surviving the impossible, we learn from nature to design and develop interactive resilience simulations that test plans, ideas and decision making that determines how effective your organisation responds when the real incident happens.</P>
        <P>Our tools are secure, scenario-based environments tailored to your organisation where you and your colleagues can explore how to overcome difficult situations whilst developing stronger collaboration.</P>
        <P>We value authenticity, precision and simplicity. Our approach is designed to help you and your teams think clearly, reflect deeply and act with intention.</P>
        <H2>Our mission</H2>
        <P>To bring resilience out of theory and into everyday life, making it something people can practise, strengthen and rely on when the worst happens.</P>
        <P>Complete the <Link to="/assessment" data-testid="about-assessment-link" className="text-white underline underline-offset-2 hover:text-[var(--urgent)] transition-colors">self-assessment audit</Link> to find out if TardigradeTeam is right for your organisation.</P>
      </>
    ),
  },
  "case-studies": {
    title: "Case studies",
    body: (
      <>
        <H2>Real-world resilience in practice</H2>
        <P>Our case studies will showcase how organisations use our simulations and microtools to strengthen decision-making, improve team responses under pressure, and build everyday resilience.</P>
        <P>We are currently preparing some exciting case studies, focusing on how scenario-based resilience training supports teams navigating complexity and high-stakes situations.</P>
        <P>Coming soon . . .</P>
        <P>We will publish selected case studies that highlight:</P>
        <UL>
          <li>The challenges organisations face.</li>
          <li>The scenarios explored.</li>
          <li>The insight gained.</li>
          <li>The practical outcomes and behaviour shifts observed.</li>
        </UL>
        <P>These will be short, clear and grounded in real work, carried out by real people, using real operating processes.</P>
        <P>If you would like to be notified when a new case study is released, keep in touch at <Mail addr="info@tardigradeteam.com" />.</P>
      </>
    ),
  },
  "privacy-policy": {
    title: "Privacy policy",
    body: (
      <>
        <P>We look after all information you share with us. We only use it to provide you with our resilience simulations and related services. We never sell your information. We take care to keep it safe and secure.</P>
        <P>This privacy policy explains what information we collect, how we use it, how long we keep it, and your rights.</P>

        <H2>1. Who we are</H2>
        <P>Tardigradeteam is an independent digital micro-studio that designs and delivers interactive resilience simulations. We are the Data Controller for the information you provide through tardigradeteam.com.</P>
        <P>If you have any questions about how your information is used, you can contact us at: <Mail addr="support@tardigradeteam.com" /></P>

        <H2>2. The information we collect</H2>
        <P>We only collect the information we need to run your simulation and manage your account. This includes:</P>
        <UL>
          <li>Account details such as your name, email address and organisation (if provided).</li>
          <li>Simulation data including your responses, decisions, notes and any reflections you enter during a scenario.</li>
          <li>Technical information — our hosting infrastructure may process your IP address and connection timestamps for security and troubleshooting. Our application records account activity such as login attempts (to protect against unauthorised access) but does not track device fingerprints or browsing behaviour.</li>
          <li>Support queries — if you contact us for help, we keep a record of your message.</li>
        </UL>
        <P>We do not collect unnecessary personal data, and we do not use automated profiling outside the simulation itself.</P>

        <H2>3. How we use your information</H2>
        <P>We use your information to:</P>
        <UL>
          <li>let you sign in and access your simulation</li>
          <li>run the interactive scenarios you've booked</li>
          <li>help you review your decisions and progress</li>
          <li>keep the platform secure and working properly</li>
          <li>improve our tools and understand how they're used (in an anonymised way)</li>
        </UL>
        <P>We only use your information when the law allows. Our lawful bases are:</P>
        <UL>
          <li>Contract — to provide the simulation you've asked for</li>
          <li>Legitimate interests — to improve the platform and keep it secure</li>
          <li>Consent — if you choose to receive optional updates or communications</li>
        </UL>

        <H2>4. How long we keep your information</H2>
        <UL>
          <li>Simulation data is kept for 30 days, then moved to an admin-only archive</li>
          <li>Archive data is kept for 90 days</li>
          <li>User accounts are deleted when your engagement ends (30 days) or when you ask us to</li>
          <li>Technical logs are kept for 90 days for security and troubleshooting</li>
        </UL>
        <P>If you ask us to delete your data, we will do so unless we need to keep it for security or legal reasons. If that happens, we'll explain why.</P>

        <H2>5. Who we share your information with</H2>
        <P>We only share your information when it's necessary to run the service. We share data with:</P>
        <UL>
          <li>Our hosting provider who processes data on our behalf and keeps it secure</li>
          <li>Our email provider for account verification and essential communication</li>
        </UL>
        <P>These organisations must treat your information with the same care that we do.</P>
        <P>We do not share your information with marketing companies, analytics platforms, or third parties for advertising.</P>
        <P>We may share information if we believe there is a serious risk to you or others, or if we are legally required to.</P>

        <H2>6. International transfers</H2>
        <P>Our application is hosted in the Netherlands through EU infrastructure. Some platform services used by our hosting provider during development or platform maintenance may process data in the United States. These transfers are protected by Standard Contractual Clauses and other safeguards. Simulation data entered by users is stored and processed within the EU infrastructure.</P>

        <H2>7. How we keep your information safe</H2>
        <P>We use a range of measures to protect your information, including:</P>
        <UL>
          <li>encryption in transit and at rest</li>
          <li>role-based access controls</li>
          <li>secure hosting</li>
          <li>regular deletion cycles</li>
          <li>minimal data collection</li>
          <li>admin-only access to archived data</li>
        </UL>
        <P>We continually review our security practices to keep your information safe.</P>

        <H2>8. Your rights</H2>
        <P>You have the right to:</P>
        <UL>
          <li>ask for a copy of the information we hold about you</li>
          <li>correct any factual errors</li>
          <li>ask us to delete your information</li>
          <li>ask us to stop using your information while we review a request</li>
          <li>object to processing based on legitimate interests</li>
          <li>request your data in a portable format</li>
          <li>withdraw consent at any time (for anything based on consent)</li>
        </UL>
        <P>If you want to use any of these rights, email us at: <Mail addr="support@tardigradeteam.com" /></P>
        <P>If you're unhappy with how we handle your data, you can contact the Information Commissioner's Office (ICO).</P>

        <H2>9. Changes to this policy</H2>
        <P>We may update this privacy policy from time to time. If we make significant changes, we'll let you know.</P>
      </>
    ),
  },
  "pricing-and-experience": {
    title: "Pricing and experience",
    body: <ComingSoon what="Our pricing options and a walkthrough of the simulation experience" />,
  },
  "security-policy": {
    title: "Security policy",
    body: <ComingSoon what="Our full security policy" />,
  },
};

export default function InfoPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const entry = CONTENT[slug];
  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (!entry) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--bg-base)] text-white overflow-x-hidden flex flex-col">
      <header className="glass fixed top-0 inset-x-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => nav("/")} data-testid="info-home-btn" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 rounded-sm bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
            <span className="font-display font-extrabold text-lg tracking-tight">TARDIGRADE</span>
          </button>
          <button data-testid="info-login-btn" onClick={() => nav("/login")}
            className="group flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 transition-colors">
            <Lock className="w-3.5 h-3.5" /> Log in
          </button>
        </div>
      </header>

      <main className="flex-1 pt-28 pb-20" data-testid={`info-page-${slug}`}>
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => nav("/")} data-testid="info-back-btn" className="flex items-center gap-2 text-[var(--txt-secondary)] hover:text-white transition-colors mb-8" style={{ fontSize: "15px" }}>
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-8">{entry.title}</h1>
          <div>{entry.body}</div>

          <div className="mt-14 pt-8 border-t border-white/10">
            <div className="text-[var(--txt-muted)] uppercase tracking-[0.15em] mb-3" style={{ fontSize: "13px" }}>Useful info</div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {INFO_LINKS.filter((l) => l.slug !== slug).map((l) => (
                <Link key={l.slug} to={`/info/${l.slug}`} data-testid={`info-related-${l.slug}`}
                  className="text-[#C9CDD2] hover:text-white transition-colors" style={{ fontSize: "16px" }}>{l.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
